import classNames from "classnames";
import WarningPopup from "components/WarningPopup";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import { noop } from "lodash";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from "utils/localStorageUtils";
import ModalUtil from "utils/modalUtil";

import ACButton from "ac-components/ac-button/ACButton";
import CloseIcon from "ac-components/ac-icons/CloseIcon";
import ACPanelResize from "ac-components/ac-panel-resize/ACPanelResize";

import "./ACModalV2.less";

type Placement = "center" | "right";

export type { Placement as ACModalV2Placement };

export type ACModalV2Props = {
  open?: boolean;
  onClose?: (state: unknown) => void;
  closable?: boolean;
  mask?: boolean;
  defaultPlacement?: Placement;
  settingsPrefixKey?: string;
  width?: number;
  children: ReactNode | ReactNode[];
  resize?: boolean;
  closeModalOnBUChange?: boolean;
  fullHeight?: boolean;
};

type WarningConfig = {
  promptMessage: ReactNode;
  okText: string;
  cancelText: string;
};

export type ModalContextType = {
  updatePlacement: (placement: Placement) => void;
  togglePlacement: () => void;
  updateClosedState: (state: unknown) => void;
  updateShowWarningOnClose: (
    show: boolean,
    config?: Partial<WarningConfig>,
  ) => void;
  placement: Placement;
  close: () => void;
  updateWidth: React.Dispatch<React.SetStateAction<number>>;
  animationCompleted: boolean;
};

const ModalContext = React.createContext<ModalContextType>({
  updatePlacement: noop,
  togglePlacement: noop,
  updateClosedState: noop,
  updateShowWarningOnClose: noop,
  placement: "center",
  close: noop,
  updateWidth: noop,
  animationCompleted: false,
});

const prefixCls = "ac-modal-v2";

const ACModalV2 = ({
  open = true,
  onClose = noop,
  closable = true,
  mask = true,
  defaultPlacement = "center",
  settingsPrefixKey,
  width: widthFromProps = 500,
  children,
  resize,
  closeModalOnBUChange = true,
  fullHeight,
}: ACModalV2Props) => {
  const { globalFilterHash } = useGlobalFilterContext();
  const globalFilterHashRef = useRef(globalFilterHash);
  const placementPrefKey = `${settingsPrefixKey}-modal-placement`;
  const widthPrefKey = `${settingsPrefixKey}-modal-width`;
  const [width, setWidth] = useState(() =>
    settingsPrefixKey
      ? (getLocalStorage(widthPrefKey, widthFromProps) as number)
      : widthFromProps,
  );
  const closedStateRef = useRef<unknown>();
  const showWarningRef = useRef(false);
  const [opened, setOpened] = useState(false);
  const [animationCompleted, setAnimationCompleted] = useState(false);
  const [placement, setPlacement] = useState(() =>
    settingsPrefixKey
      ? (getLocalStorage(placementPrefKey, defaultPlacement) as Placement)
      : defaultPlacement,
  );
  const modalRef = useRef<HTMLDivElement>(null);
  const [warningConfig, setWarningConfig] = useState<WarningConfig>({
    promptMessage: "Any unsaved changes will be lost.",
    okText: "Proceed",
    cancelText: "Cancel",
  });

  const exitModal = useCallback(() => {
    setOpened(false);
    setTimeout(() => onClose(closedStateRef.current), 150);
  }, [onClose]);

  const close = useCallback(() => {
    if (showWarningRef.current) {
      ModalUtil.show({
        content: (
          <WarningPopup
            //@ts-expect-error : should accept promptMessage prop
            promptMessage={warningConfig.promptMessage}
            okText={warningConfig.okText}
            onOk={exitModal}
            cancelBtnText={warningConfig.cancelText}
          />
        ),
      });
    } else {
      exitModal();
    }
  }, [exitModal, warningConfig]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };

    if (open) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, close]);

  const contextValue = useMemo<ModalContextType>(
    () => ({
      updatePlacement: (placement) => setPlacement(placement),
      togglePlacement: () =>
        setPlacement((placement) =>
          placement === "center" ? "right" : "center",
        ),
      updateClosedState: (state) => (closedStateRef.current = state),
      updateShowWarningOnClose: (show, config) => {
        showWarningRef.current = show;
        setWarningConfig((prev) => ({ ...prev, ...config }));
      },
      placement,
      close,
      updateWidth: setWidth,
      animationCompleted,
    }),
    [placement, close, animationCompleted],
  );

  useEffect(() => {
    if (closeModalOnBUChange) {
      if (globalFilterHashRef.current !== globalFilterHash) {
        globalFilterHashRef.current = globalFilterHash;
        close();
      }
    }
  }, [globalFilterHash, close, closeModalOnBUChange]);

  useEffect(() => {
    setTimeout(() => setOpened(open));
  }, [open]);

  useEffect(() => {
    if (defaultPlacement === placement) {
      deleteLocalStorage(placementPrefKey);
    } else {
      setLocalStorage(placementPrefKey, placement);
    }
    if (widthFromProps === width) {
      deleteLocalStorage(widthPrefKey);
    } else {
      setLocalStorage(widthPrefKey, width);
    }
  }, [
    placement,
    placementPrefKey,
    width,
    defaultPlacement,
    widthPrefKey,
    widthFromProps,
  ]);

  useEffect(() => {
    if (opened) {
      const onTransitionEnd = (e: TransitionEvent) => {
        if (e.target === modalRef.current && e.propertyName === "transform") {
          setAnimationCompleted(true);
          modalRef.current?.removeEventListener(
            "transitionend",
            onTransitionEnd,
          );
        }
      };
      modalRef.current?.addEventListener("transitionend", onTransitionEnd);
      return () =>
        modalRef.current?.removeEventListener("transitionend", onTransitionEnd);
    }
  }, [opened]);

  const container =
    (document.getElementsByClassName(
      "app-content-wrapper",
    )[0] as HTMLElement) || document.body;

  return createPortal(
    <div
      className={classNames(`${prefixCls}-root ${prefixCls}-${placement}`, {
        [`${prefixCls}-masked`]: mask,
        opened,
      })}
    >
      <div className={`${prefixCls}-wrapper`} style={{ width }} ref={modalRef}>
        <ACPanelResize
          resizeFrom={placement === "right" ? "left" : "both"}
          disabled={!resize}
          onChange={(w) => setWidth(Math.max(w, 800))}
        >
          {closable ? (
            <ACButton
              className={`${prefixCls}-close-button`}
              iconOnly
              type="ghost"
              onClick={close}
            >
              <CloseIcon />
            </ACButton>
          ) : null}
          <div
            className={classNames(`${prefixCls}-container`, {
              [`${prefixCls}-container-full-height`]: fullHeight,
            })}
          >
            <ModalContext.Provider value={contextValue}>
              {children}
            </ModalContext.Provider>
          </div>
        </ACPanelResize>
      </div>
    </div>,
    container,
  );
};

export default ACModalV2;

ACModalV2.Title = (
  props: React.HTMLAttributes<HTMLDivElement> & { closable?: boolean },
) => {
  return (
    <div
      {...props}
      className={classNames(props.className, `${prefixCls}-title`)}
    />
  );
};
ACModalV2.Footer = (props: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      {...props}
      className={classNames(props.className, `${prefixCls}-footer`)}
    />
  );
};

ACModalV2.useModalSettings = () => useContext(ModalContext);
